// eslint-disable-next-line no-restricted-imports
import React, { useState } from 'react';
import { Box, Button, Flex, Link, Text, useToasts } from '@applyboard/crystal-ui';
import { useTranslation } from '@applyboard/ui-utils';
import base64ToUInt8ByteArray from '../../../common/base64ToUInt8ByteArray';
import { useDownloadGicForm } from '../../../data/rbcGic';
import { RbcGicFormTypeV2 } from '../types';
import DownloadIcon from './DownloadIcon';
import TadaImage from './TadaImage';
import { RBC_GIC_CONFIRMATION_BACK_TO_HOMEPAGE_LINK } from './consts';

interface ConfirmationStepProps {
  formState: RbcGicFormTypeV2;
}

function renderPDFBase64(base64PDF: string) {
  const parsedBase64Content = base64PDF.split(',');
  const base64DataWithoutUri =
    parsedBase64Content.length === 2 ? parsedBase64Content[1] : base64PDF;
  const file = new Blob([base64ToUInt8ByteArray(base64DataWithoutUri)], {
    type: 'application/pdf;base64',
  });
  const fileURL = URL.createObjectURL(file);
  window.open(fileURL);
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({ formState }) => {
  const toast = useToasts();
  const [downloadDisabled, setDownloadDisabled] = useState(false);
  const downloadGicForm = useDownloadGicForm(formState.id ?? '', formState.verificationCode ?? '');

  const { t } = useTranslation();
  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        minHeight: '100vh',
      }}
    >
      <Flex
        direction="column"
        align="center"
        gap={{
          lg: 12,
          sm: 8,
          xs: 4,
        }}
      >
        <Box pt={32}>
          <TadaImage />
        </Box>
        <Box>
          <Text intent="primary" variant="headlineL" align="center">
            {t('rbcv2.confirmation.thankYouLine1')}
          </Text>
          <Text intent="primary" variant="headlineL" align="center">
            {t('rbcv2.confirmation.thankYouLine2', {
              gicId: formState.gicId ?? '',
            })}
          </Text>
        </Box>
        <Box
          pl={{
            xs: 4,
            sm: 8,
            md: 24,
            lg: 28,
            xl: 36,
          }}
          pr={{
            xs: 4,
            sm: 8,
            md: 24,
            lg: 28,
            xl: 36,
          }}
        >
          <Text intent="secondary" variant="bodyS" align="center">
            {t('rbcv2.confirmation.successAndNextSteps1')}
          </Text>
          <Text intent="secondary" variant="labelM" align="center">
            {t('rbcv2.confirmation.successAndNextSteps2')}
          </Text>
          <Text intent="secondary" variant="bodyS" align="center">
            {t('rbcv2.confirmation.successAndNextSteps3')}
          </Text>
        </Box>
        <Box>
          <Button
            leadIcon={DownloadIcon}
            intent="primary"
            loading={downloadDisabled}
            disabled={downloadDisabled}
            onClick={() => {
              setDownloadDisabled(true);
              downloadGicForm().then(
                gicFormBase64Uri => {
                  renderPDFBase64(gicFormBase64Uri);
                  setDownloadDisabled(false);
                },
                error => {
                  toast.negative(error);
                  setDownloadDisabled(false);
                },
              );
            }}
          >
            {t('rbcv2.confirmation.downloadForm')}
          </Button>
        </Box>
        <Box>
          <Link
            href={RBC_GIC_CONFIRMATION_BACK_TO_HOMEPAGE_LINK}
            mode="override_user_choice_and_open_in_new_tab"
          >
            <Text intent="secondary" variant="bodyS">
              {t('rbcv2.confirmation.backToHomepage')}
            </Text>
          </Link>
        </Box>
      </Flex>
    </div>
  );
};

export default ConfirmationStep;
